import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import DynamicHmiPanel from '../components/DynamicHmiPanel';
import MarpossBench from '../components/MarpossBench';
import { machineDetailPath, machineListPath } from '../utils/machineRoutes';
import { DEFAULT_COMPANY } from '../constants/company';

const API_BASE = '/machinesim/api';

function MachinePage({ routePrefix = '' }) {
  const { machineName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companySlug = searchParams.get('company') || DEFAULT_COMPANY;
  const machineTypeCode =
    searchParams.get('type') ||
    import.meta.env.VITE_DEFAULT_MACHINE_TYPE ||
    'CNC_LATHE';
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allMachines, setAllMachines] = useState([]);
  const [benchOpen, setBenchOpen] = useState(false);
  const [gaugeSurface, setGaugeSurface] = useState('spc');
  // MES job control was removed (engine is the single driver). The Dynamic HMI's E-stop
  // confirm ref defaults to always-confirm; nothing overrides it now.
  const mesEstopUserConfirmRef = useRef(async () => true);

  // Fetch machine data
  useEffect(() => {
    const fetchMachine = async () => {
      try {
        // MUST be company-scoped: a bare /machines falls back to the 'default' company (Chentronix,
        // bare machine names), so a rassini-prefixed name like `rassini-LATHE-01` would never be
        // found → "Machine not found". Scope the fetch the same way the Live Floor does.
        const res = await axios.get(`${API_BASE}/machines?company=${encodeURIComponent(companySlug)}`);
        const machines = res.data.machines;
        setAllMachines(machines);

        const foundMachine = machines.find(m => m.name === machineName);
        if (foundMachine) {
          setMachine(foundMachine);
          setError(null);
        } else {
          setError('Machine not found');
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchMachine();
  }, [machineName, companySlug]);

  useEffect(() => {
    // A quality station lands on SPC every time it is entered. Raw CNC remains a secondary
    // engineering/debug surface, never the default operator view.
    setGaugeSurface('spc');
  }, [machineName]);

  // Keyboard navigation for switching machines
  useEffect(() => {
    if (allMachines.length <= 1) return;

    const handleKeyPress = (event) => {
      if (benchOpen) return;
      const currentIndex = allMachines.findIndex(m => m.name === machineName);
      if (currentIndex === -1) return;

      if (event.key === 'ArrowRight') {
        const nextIndex = (currentIndex + 1) % allMachines.length;
        navigate(machineDetailPath(routePrefix, allMachines[nextIndex].name));
      } else if (event.key === 'ArrowLeft') {
        const prevIndex = (currentIndex - 1 + allMachines.length) % allMachines.length;
        navigate(machineDetailPath(routePrefix, allMachines[prevIndex].name));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [allMachines, machineName, navigate, routePrefix, benchOpen]);

  // Close the Marposs bench on Escape
  useEffect(() => {
    if (!benchOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setBenchOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [benchOpen]);

  const currentIndex = allMachines.findIndex(m => m.name === machineName);
  const isGaugeStation = machine?.machineType === 'GAUGE' || /(?:^|-)BANCO$/i.test(machineName);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allMachines.length - 1;

  const goToPrev = () => {
    if (hasPrev) {
      navigate(machineDetailPath(routePrefix, allMachines[currentIndex - 1].name));
    }
  };

  const goToNext = () => {
    if (hasNext) {
      navigate(machineDetailPath(routePrefix, allMachines[currentIndex + 1].name));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-900 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-xl">Loading machine...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-900 text-white">
        <p className="text-2xl text-red-500 mb-4">Error: {error}</p>
        <Link
          to={machineListPath(routePrefix)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-800">
      {/* Top Navigation Bar */}
      <div className="bg-gray-900 border-b border-gray-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={machineListPath(routePrefix)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Dashboard</span>
            </Link>
            <span className="text-gray-600">|</span>
            <span className="text-white font-medium">{machineName}</span>
          </div>

          <div className="flex items-center gap-3">
            {isGaugeStation ? (
              <div className="flex items-center rounded-lg border border-gray-700 bg-gray-950/40 p-1">
                <button
                  onClick={() => setGaugeSurface('spc')}
                  className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${
                    gaugeSurface === 'spc' ? 'bg-emerald-700 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                  title="SPC quality overview, trends and correction actions"
                >
                  SPC
                </button>
                <button
                  onClick={() => setGaugeSurface('cnc')}
                  className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${
                    gaugeSurface === 'cnc' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'
                  }`}
                  title="Raw CNC / engineering control view"
                >
                  Raw control
                </button>
              </div>
            ) : (
              <button
                onClick={() => setBenchOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 rounded transition-colors"
                title="Marposs bench — measurements, suggested offset corrections, approval"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                </svg>
                Marposs Bench
              </button>
            )}
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <span>Machine {currentIndex + 1} of {allMachines.length}</span>
              <span className="text-gray-600">•</span>
              <span>Use ← → arrows to navigate</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative flex justify-center p-4">
        {/* Left Arrow */}
        {allMachines.length > 1 && (
          <button
            onClick={goToPrev}
            disabled={!hasPrev}
            className={`fixed left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all z-10 ${
              hasPrev
                ? 'bg-gray-700 hover:bg-gray-600 border-2 border-gray-600 hover:border-gray-500 text-white shadow-lg'
                : 'bg-gray-800 border-2 border-gray-700 text-gray-600 cursor-not-allowed'
            }`}
            title="Previous Machine (Left Arrow)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {isGaugeStation && gaugeSurface === 'spc' ? (
          <div className="w-full max-w-[1550px] px-12">
            <MarpossBench key={machineName} machineName={machineName} embedded />
          </div>
        ) : (
          <>
            {/* On the gauge, the real FANUC/i-Series surface stays intact but secondary. */}
            <DynamicHmiPanel
              key={machineName}
              machine={machine}
              typeCode={machineTypeCode}
              company={companySlug}
              initialPage={searchParams.get('hmiPage')}
              mesEstopUserConfirmRef={mesEstopUserConfirmRef}
            />
          </>
        )}

        {/* Right Arrow */}
        {allMachines.length > 1 && (
          <button
            onClick={goToNext}
            disabled={!hasNext}
            className={`fixed right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all z-10 ${
              hasNext
                ? 'bg-gray-700 hover:bg-gray-600 border-2 border-gray-600 hover:border-gray-500 text-white shadow-lg'
                : 'bg-gray-800 border-2 border-gray-700 text-gray-600 cursor-not-allowed'
            }`}
            title="Next Machine (Right Arrow)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {benchOpen && <MarpossBench machineName={machineName} onClose={() => setBenchOpen(false)} />}
    </div>
  );
}

export default MachinePage;
